import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  alpha,
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API from '../api/axios';
import { getImageUrl, FALLBACK_IMAGE } from '../utils/imageUtils';
import useCategories from '../hooks/useCategories';

const CATEGORY_COLOR_MAP = {
  Domestic: { color: '#3b82f6', bg: alpha('#3b82f6', 0.1) },
  International: { color: '#10b981', bg: alpha('#10b981', 0.1) },
  Religious: { color: '#f59e0b', bg: alpha('#f59e0b', 0.1) },
  Wildlife: { color: '#22c55e', bg: alpha('#22c55e', 0.1) },
};

const getCategoryColor = (cat) =>
  CATEGORY_COLOR_MAP[cat] || CATEGORY_COLOR_MAP.Domestic;

const ExclusiveOffersPage = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Dynamic categories from backend
  const { categories: VALID_TABS } = useCategories(true); // includeAll = true

  const activeTab = searchParams.get('tab') && VALID_TABS.includes(searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'All';

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/offers?active=1');
      setOffers(Array.isArray(data) ? data : []);
    } catch {
      setError('Failed to load exclusive offers. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const filteredOffers = activeTab === 'All'
    ? offers
    : offers.filter(o => o.category === activeTab);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8faff' }}>
      {/* Hero Banner - Compact */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          py: { xs: 3, md: 4 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -40,
            right: -40,
            width: 160,
            height: 160,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.06)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -60,
            left: -20,
            width: 200,
            height: 200,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.04)',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: 50,
                px: 2.5,
                py: 0.5,
                mb: 2,
              }}
            >
              <LocalOfferIcon sx={{ fontSize: 14, color: 'white' }} />
              <Typography variant="caption" sx={{ color: 'white', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                Exclusive Deals
              </Typography>
            </Box>

            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: 'white',
                fontSize: { xs: '1.5rem', md: '2rem' },
                lineHeight: 1.2,
                mb: 1,
                textShadow: '0 2px 20px rgba(0,0,0,0.2)',
              }}
            >
              {activeTab === 'All' ? 'Holiday Offers' : `${activeTab} Holiday Offers`}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.8)',
                fontWeight: 300,
                maxWidth: 520,
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: { xs: '0.85rem', md: '0.95rem' },
              }}
            >
              Browse all our exclusive, handpicked holiday deals for your next unforgettable journey.
            </Typography>

            {!loading && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', mt: 1, display: 'block' }}>
                {filteredOffers.length} offer{filteredOffers.length !== 1 ? 's' : ''} available
              </Typography>
            )}
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 7 } }}>
        {/* Offers Grid */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
            <CircularProgress sx={{ color: '#667eea' }} size={48} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ maxWidth: 500, mx: 'auto', borderRadius: 3 }}>{error}</Alert>
        ) : filteredOffers.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 12 }}>
            <LocalOfferIcon sx={{ fontSize: 72, color: '#cbd5e1', mb: 2 }} />
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No Offers Available
            </Typography>
            <Typography variant="body1" color="text.disabled">
              There are no {activeTab !== 'All' ? activeTab.toLowerCase() : ''} exclusive offers at the moment. Check back soon!
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {filteredOffers.map((offer, idx) => {
              const cat = getCategoryColor(offer.category);
              return (
                <Grid item xs={12} sm={6} md={4} key={offer.id}>
                  <Card
                    id={`offer-card-${offer.id}`}
                    sx={{
                      borderRadius: 4,
                      overflow: 'hidden',
                      boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
                      transition: 'all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
                      animation: `fadeSlideUp 0.4s ease ${(idx % 6) * 0.06}s both`,
                      '@keyframes fadeSlideUp': {
                        from: { opacity: 0, transform: 'translateY(28px)' },
                        to: { opacity: 1, transform: 'translateY(0)' },
                      },
                      '&:hover': {
                        transform: 'translateY(-10px)',
                        boxShadow: '0 24px 64px rgba(102,126,234,0.18)',
                      },
                    }}
                  >
                    <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                      <CardMedia
                        component="img"
                        height="240"
                        image={getImageUrl(offer.image_url) || FALLBACK_IMAGE}
                        alt={offer.description}
                        onError={(e) => { e.target.src = FALLBACK_IMAGE; }}
                        sx={{
                          objectFit: 'cover',
                          transition: 'transform 0.6s ease',
                          '&:hover': { transform: 'scale(1.08)' },
                        }}
                      />
                      {/* Bottom gradient */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: '55%',
                          background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 100%)',
                        }}
                      />
                      {/* Category badge */}
                      <Box sx={{ position: 'absolute', top: 14, left: 14 }}>
                        <Chip
                          label={offer.category}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.9)',
                            color: cat.color,
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            height: 24,
                            backdropFilter: 'blur(8px)',
                          }}
                        />
                      </Box>
                      {/* Exclusive badge */}
                      <Box sx={{ position: 'absolute', top: 14, right: 14 }}>
                        <Chip
                          label="Exclusive"
                          size="small"
                          sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '0.65rem',
                            height: 24,
                          }}
                        />
                      </Box>
                    </Box>

                    <CardContent sx={{ p: 3 }}>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{
                          lineHeight: 1.75,
                          display: '-webkit-box',
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {offer.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default ExclusiveOffersPage;